import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import { ApiKeysPanel } from './ApiKeysPanel';
import { VOICES } from '../constants/voices';
import {
  getOrCreateSpeechBus,
  playAudio,
} from '../hooks/useAudioPlayer';
import { VoicePresetsPanel } from './VoicePresetsPanel';
import { ZeroVoiceTab } from './ZeroVoiceTab';
import { BackupTab } from './BackupTab';
import { MemoryAdvTab } from './MemoryAdvTab';
import { HomunculusTab } from './HomunculusTab';
import { PhantomTab } from './PhantomTab';
import { GadflyTab } from './GadflyTab';
import { WarpTab } from './WarpTab';
import { LaughterTab } from './LaughterTab';
import { ParallaxTab } from './ParallaxTab';
import {
  listDotProfiles,
  uploadDotProfile,
  deleteDotProfile,
  type DotProfileLite,
} from '../api/dotProfiles';

const DotTabSupershapeDot = lazy(() => import('./SupershapeDot'));

type Tab =
  | 'apikeys'
  | 'personas'
  | 'voice'
  | 'zerovoice'
  | 'memory'
  | 'memory-adv'
  | 'homunculus'
  | 'phantom'
  | 'gadfly'
  | 'warp'
  | 'laughter'
  | 'parallax'
  | 'permissions'
  | 'backup'
  | 'dot';

const PERMISSION_TOGGLES: Array<{ key: string; label: string; note?: string }> =
  [
    { key: 'permission_web_search', label: 'Web search (Gemini grounding)' },
    { key: 'permission_image_gen', label: 'Image generation' },
    {
      key: 'permission_gpt_image_gen',
      label: 'GPT image generation (GPT-image-tool)',
      note: 'requires an OpenRouter API key; uses openai/gpt-5.4-image-2 via OpenRouter. Runs alongside the existing Gemini image tool.',
    },
    { key: 'permission_music_gen', label: 'Music generation (Lyria)' },
    { key: 'permission_speech_clip', label: 'Speech clip generation' },
    { key: 'permission_describe_image', label: 'Image description (vision)' },
    { key: 'permission_update_memory', label: 'Memory updates' },
    {
      key: 'permission_live_voice',
      label: 'Live voice mode (audio-to-audio)',
      note: 'enables a LIVE button in the header; uses gemini-3.1-flash-live-preview',
    },
    {
      key: 'permission_video_gen',
      label: 'Video generation (kling-video-tool)',
      note: 'requires an OpenRouter API key; videos generate in the background (1–3 min per clip) and appear in the Video Gallery',
    },
    {
      key: 'permission_veo_video_gen',
      label: 'Veo video generation (veo-video-tool)',
      note: 'uses the configured Gemini API key. Generates short videos with Google Veo 3.1 Fast (1–3 min per clip). Cards appear in the Video Gallery with a "veo" badge.',
    },
    // Stage 44: LIBRARIAN-Wiki master flag + per-tool persona gates.
    {
      key: 'wiki_enabled',
      label: 'LIBRARIAN-Wiki (master)',
      note: 'Enables the wiki module. Drains wiki_event_log on the cron tick and lights up the WIKI button. Off by default — turn this on first, then enable individual persona tools below.',
    },
    {
      key: 'permission_wiki_query',
      label: 'Persona tool: wiki_query',
      note: 'Lets the chat persona ask the wiki natural-language questions and receive cited answers. Requires wiki_enabled.',
    },
    {
      key: 'permission_wiki_search',
      label: 'Persona tool: wiki_search',
      note: 'Lets the persona run hybrid search and receive ranked hits without LLM synthesis.',
    },
    {
      key: 'permission_wiki_get_page',
      label: 'Persona tool: wiki_get_page',
      note: 'Lets the persona fetch a specific wiki page by slug.',
    },
    {
      key: 'permission_librarian_find',
      label: 'Persona tool: librarian_find',
      note: 'Lets the persona browse the catalogue (uploaded_images, generated_documents, generated_images, music_tracks, speech_tracks, video_generations).',
    },
    {
      key: 'permission_librarian_retrieve',
      label: 'Persona tool: librarian_retrieve',
      note: 'Lets the persona fetch a specific record\'s text body or a presigned blob URL by record_id.',
    },
    {
      key: 'permission_librarian_store',
      label: 'Persona tool: librarian_store',
      note: 'Lets the persona save markdown content as a generated_documents row, optionally tagged for wiki ingest.',
    },
  ];

const API_BASE = import.meta.env.VITE_API_BASE as string;

interface PersonaListItem {
  id: string;
  name: string;
  full_name: string | null;
  description: string | null;
  voice: string;
  mood: string | null;
  updated_at: number;
}

interface PersonaListResponse {
  personas: PersonaListItem[];
}

interface MemoryResponse {
  memory: { content: string; updated_at: number };
}

interface ActivePersonaResponse {
  persona: { id: string };
}

export function SettingsModal({
  onClose,
  onChanged,
  currentConversationId,
}: {
  onClose: () => void;
  onChanged: () => void;
  currentConversationId?: string | null;
}) {
  const [tab, setTab] = useState<Tab>('apikeys');

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal modal-settings">
        <header className="modal-header">
          <h2>Settings</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <nav className="modal-tabs">
          <button
            className={tab === 'apikeys' ? 'tab active' : 'tab'}
            onClick={() => setTab('apikeys')}
          >
            API Keys
          </button>
          <button
            className={tab === 'personas' ? 'tab active' : 'tab'}
            onClick={() => setTab('personas')}
          >
            Personas
          </button>
          <button
            className={tab === 'voice' ? 'tab active' : 'tab'}
            onClick={() => setTab('voice')}
          >
            Voice
          </button>
          <button
            className={tab === 'zerovoice' ? 'tab active' : 'tab'}
            onClick={() => setTab('zerovoice')}
          >
            ZeroVoice
          </button>
          <button
            className={tab === 'memory' ? 'tab active' : 'tab'}
            onClick={() => setTab('memory')}
          >
            Memory
          </button>
          <button
            className={tab === 'memory-adv' ? 'tab active' : 'tab'}
            onClick={() => setTab('memory-adv')}
          >
            Memory (advanced)
          </button>
          <button
            className={tab === 'homunculus' ? 'tab active' : 'tab'}
            onClick={() => setTab('homunculus')}
          >
            Homunculus
          </button>
          <button
            className={tab === 'phantom' ? 'tab active' : 'tab'}
            onClick={() => setTab('phantom')}
          >
            Phantom
          </button>
          <button
            className={tab === 'gadfly' ? 'tab active' : 'tab'}
            onClick={() => setTab('gadfly')}
          >
            Gadfly
          </button>
          <button
            className={tab === 'warp' ? 'tab active' : 'tab'}
            onClick={() => setTab('warp')}
          >
            WARP
          </button>
          <button
            className={tab === 'laughter' ? 'tab active' : 'tab'}
            onClick={() => setTab('laughter')}
          >
            Laughter
          </button>
          <button
            className={tab === 'parallax' ? 'tab active' : 'tab'}
            onClick={() => setTab('parallax')}
          >
            Parallax
          </button>
          <button
            className={tab === 'permissions' ? 'tab active' : 'tab'}
            onClick={() => setTab('permissions')}
          >
            Permissions
          </button>
          <button
            className={tab === 'backup' ? 'tab active' : 'tab'}
            onClick={() => setTab('backup')}
          >
            Backup
          </button>
          <button
            className={tab === 'dot' ? 'tab active' : 'tab'}
            onClick={() => setTab('dot')}
          >
            DOT
          </button>
        </nav>
        <section className="modal-body">
          {tab === 'apikeys' ? <ApiKeysTab onChanged={onChanged} /> : null}
          {tab === 'personas' ? <PersonasTab onChanged={onChanged} /> : null}
          {tab === 'voice' ? <VoiceTab onChanged={onChanged} /> : null}
          {tab === 'zerovoice' ? <ZeroVoiceTab onChanged={onChanged} /> : null}
          {tab === 'memory' ? <MemoryTab /> : null}
          {tab === 'memory-adv' ? <MemoryAdvTab onChanged={onChanged} /> : null}
          {tab === 'homunculus' ? (
            <HomunculusTab
              onChanged={onChanged}
              currentConversationId={currentConversationId}
            />
          ) : null}
          {tab === 'phantom' ? <PhantomTab onChanged={onChanged} /> : null}
          {tab === 'gadfly' ? (
            <GadflyTab
              onChanged={onChanged}
              currentConversationId={currentConversationId}
            />
          ) : null}
          {tab === 'warp' ? (
            <WarpTab
              onChanged={onChanged}
              currentConversationId={currentConversationId}
            />
          ) : null}
          {tab === 'laughter' ? <LaughterTab onChanged={onChanged} /> : null}
          {tab === 'parallax' ? (
            <ParallaxTab
              onChanged={onChanged}
              currentConversationId={currentConversationId}
            />
          ) : null}
          {tab === 'permissions' ? (
            <PermissionsTab onChanged={onChanged} />
          ) : null}
          {tab === 'backup' ? <BackupTab onChanged={onChanged} /> : null}
          {tab === 'dot' ? <DotTab onChanged={onChanged} /> : null}
        </section>
        <footer className="modal-footer muted small">
          Shortcuts: <kbd>Esc</kbd> close · <kbd>Cmd</kbd>/<kbd>Ctrl</kbd>
          +<kbd>,</kbd> toggle Settings
        </footer>
      </div>
    </div>
  );
}

interface PermissionsSettingsResponse {
  settings: Record<string, string>;
}

function PermissionsTab({ onChanged }: { onChanged: () => void }) {
  const [state, setState] = useState<Record<string, boolean> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiGet<PermissionsSettingsResponse>('/api/settings');
        const s = res.settings || {};
        const out: Record<string, boolean> = {};
        for (const p of PERMISSION_TOGGLES) {
          out[p.key] = s[p.key] === 'true';
        }
        setState(out);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const toggle = async (key: string): Promise<void> => {
    if (!state) return;
    const next = { ...state, [key]: !state[key] };
    setState(next);
    setBusy(true);
    setErr(null);
    try {
      await apiJson('PUT', '/api/settings', {
        [key]: next[key] ? 'true' : 'false',
      });
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      // revert
      setState(state);
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <div className="muted">Loading…</div>;

  return (
    <div className="panel">
      <h3>Permissions</h3>
      <p className="muted small">
        Disabled tools are not declared to Gemini. Web search enables
        Gemini's native Google Search grounding — the assistant cites
        sources inline when used.
      </p>
      <ul className="permission-list">
        {PERMISSION_TOGGLES.map((p) => (
          <li key={p.key}>
            <label className="voice-toggle-row">
              <input
                type="checkbox"
                checked={state[p.key]}
                onChange={() => void toggle(p.key)}
                disabled={busy}
              />
              <span>{p.label}</span>
              {p.note ? (
                <span className="muted small"> — {p.note}</span>
              ) : null}
            </label>
          </li>
        ))}
      </ul>
      {err ? <div className="status-missing">Error: {err}</div> : null}
    </div>
  );
}

function ApiKeysTab({ onChanged: _onChanged }: { onChanged: () => void }) {
  // ApiKeysPanel manages its own status; the AuthedApp re-fetches `/api/init`
  // when the modal closes, which picks up any key change.
  return <ApiKeysPanel />;
}

function PersonasTab({ onChanged }: { onChanged: () => void }) {
  const [personas, setPersonas] = useState<PersonaListItem[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [importText, setImportText] = useState('');
  const [importErr, setImportErr] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    setErr(null);
    try {
      const list = await apiGet<PersonaListResponse>('/api/personas');
      setPersonas(list.personas);
      try {
        const active = await apiGet<ActivePersonaResponse>(
          '/api/personas/active',
        );
        setActiveId(active.persona.id);
      } catch {
        setActiveId(null);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const setActive = async (id: string): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await apiJson('PUT', '/api/personas/active', { personaId: id });
      setActiveId(id);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const importPersona = async (): Promise<void> => {
    setImportErr(null);
    setBusy(true);
    try {
      let parsed: unknown;
      try {
        parsed = JSON.parse(importText);
      } catch (err) {
        throw new Error(
          `Invalid JSON: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      const p = parsed as {
        id?: string;
        name?: string;
        full_name?: string;
        description?: string;
        voice?: string;
        mood?: string | null;
        greeting_template?: string;
        profile?: unknown;
      };
      if (!p.id || !p.name || !p.profile) {
        throw new Error('JSON must contain id, name, and profile');
      }
      await apiJson('POST', '/api/personas', p);
      setImportText('');
      await refresh();
      onChanged();
    } catch (e) {
      setImportErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <div className="panel">
        <h3>Available personas</h3>
        {err ? <div className="status-missing">Error: {err}</div> : null}
        {personas === null ? (
          <div className="muted">Loading…</div>
        ) : personas.length === 0 ? (
          <div className="muted">No personas yet.</div>
        ) : (
          <ul className="persona-list">
            {personas.map((p) => (
              <li key={p.id} className="persona-list-item">
                <div>
                  <div className="persona-list-name">
                    {p.name}
                    {p.id === activeId ? (
                      <span className="badge"> active</span>
                    ) : null}
                  </div>
                  <div className="muted small">
                    {p.description || p.full_name || p.id}
                  </div>
                </div>
                <div className="row">
                  {p.id === activeId ? (
                    <span className="muted">in use</span>
                  ) : (
                    <button
                      onClick={() => void setActive(p.id)}
                      disabled={busy}
                    >
                      Set active
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="panel">
        <h3>Import persona JSON</h3>
        <p className="muted">
          Paste a full persona JSON ({'{ id, name, profile, ... }'}).
        </p>
        <textarea
          rows={8}
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          placeholder='{"id":"my-persona-v1","name":"…","profile":{…}}'
        />
        {importErr ? (
          <div className="status-missing">Error: {importErr}</div>
        ) : null}
        <div className="row">
          <button
            className="btn-primary"
            onClick={() => void importPersona()}
            disabled={busy || !importText.trim()}
          >
            Import
          </button>
        </div>
      </div>
      <MoodDynamicsPanel onChanged={onChanged} />
    </div>
  );
}

interface MoodSettingsResponse {
  settings: Record<string, string>;
}

function MoodDynamicsPanel({ onChanged }: { onChanged: () => void }) {
  const [dynamic, setDynamic] = useState<boolean>(false);
  const [alpha, setAlpha] = useState<number>(0.7);
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiGet<MoodSettingsResponse>('/api/settings');
        const s = res.settings || {};
        setDynamic(s.mood_dynamic === 'true');
        const a = parseFloat(s.mood_accumulator_alpha ?? '0.7');
        setAlpha(Number.isFinite(a) ? Math.max(0, Math.min(1, a)) : 0.7);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const saveSetting = async (key: string, value: string): Promise<void> => {
    setErr(null);
    try {
      await apiJson('PUT', '/api/settings', { [key]: value });
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  if (!loaded) return null;

  return (
    <div className="panel">
      <h3>Mood dynamics (v2)</h3>
      <p className="muted small">
        The model emits an affect signal (valence, arousal) at the end of
        each reply, which feeds a per-conversation accumulator on a
        continuous 2D circumplex. Exponential smoothing damps single-turn
        spikes. Live voice mode continues to use the daily baseline only.
      </p>

      <label className="voice-toggle-row" style={{ marginTop: 8 }}>
        <input
          type="checkbox"
          checked={dynamic}
          onChange={(e) => {
            setDynamic(e.target.checked);
            void saveSetting(
              'mood_dynamic',
              e.target.checked ? 'true' : 'false',
            );
          }}
        />
        <span>Dynamic mood selection</span>
        <span className="muted small">
          {' '}
          — model-emitted tag + accumulator
        </span>
      </label>

      <div className="mood-weight-row">
        <div className="mood-weight-label">
          Accumulator α: <strong>{alpha.toFixed(2)}</strong>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={alpha}
          disabled={!dynamic}
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={alpha}
          onChange={(e) => setAlpha(parseFloat(e.target.value))}
          onMouseUp={(e) =>
            void saveSetting(
              'mood_accumulator_alpha',
              (e.currentTarget as HTMLInputElement).value,
            )
          }
          onTouchEnd={(e) =>
            void saveSetting(
              'mood_accumulator_alpha',
              (e.currentTarget as HTMLInputElement).value,
            )
          }
        />
        <div className="muted small">
          0.00 = no memory (pure per-turn) · 1.00 = full freeze (baseline
          only) · 0.70 = default; balanced drift
        </div>
      </div>

      <p className="muted small" style={{ marginTop: 10 }}>
        <em>Note:</em> the Stage 4 affect lexicon and blend-weight controls
        have been superseded by this v2 system. The model classifies affect
        directly, which works across any language.
      </p>

      {err ? <div className="status-missing small">{err}</div> : null}
    </div>
  );
}

function MemoryTab() {
  const [content, setContent] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiGet<MemoryResponse>('/api/memory');
        setContent(res.memory?.content ?? '');
        setSaved(res.memory?.content ?? '');
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      }
    })();
  }, []);

  const save = async (): Promise<void> => {
    if (content === null) return;
    setBusy(true);
    setErr(null);
    try {
      await apiJson('PUT', '/api/memory', { content });
      setSaved(content);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await apiJson('POST', '/api/memory/reset');
      const res = await apiGet<MemoryResponse>('/api/memory');
      setContent(res.memory?.content ?? '');
      setSaved(res.memory?.content ?? '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const dirty = content !== null && content !== saved;

  return (
    <div className="panel">
      <h3>Memory</h3>
      <p className="muted">
        Markdown that gets prepended to every persona system prompt.
      </p>
      <textarea
        rows={16}
        value={content ?? ''}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Loading…"
        disabled={content === null}
        spellCheck={false}
      />
      {err ? <div className="status-missing">Error: {err}</div> : null}
      <div className="row">
        <button
          className="btn-primary"
          onClick={() => void save()}
          disabled={busy || !dirty}
        >
          Save
        </button>
        <button onClick={() => void reset()} disabled={busy}>
          Reset to template
        </button>
        {dirty ? <span className="muted">unsaved changes</span> : null}
      </div>
    </div>
  );
}

interface SettingsResponse {
  settings: Record<string, string>;
}

interface SpeakResponse {
  id: string;
  r2Key: string;
  signedUrl: string;
  sizeBytes: number;
  durationMs: number;
  voice: string;
  model: string;
}

function VoiceTab({ onChanged }: { onChanged: () => void }) {
  const [speakResponses, setSpeakResponses] = useState<boolean>(true);
  const [defaultVoice, setDefaultVoice] = useState<string>('Kore');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiGet<SettingsResponse>('/api/settings');
        const s = res.settings || {};
        setSpeakResponses(s.speak_responses !== 'false');
        setDefaultVoice(s.default_tts_voice || 'Kore');
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const saveSpeakResponses = async (next: boolean): Promise<void> => {
    setBusy(true);
    setErr(null);
    setSpeakResponses(next);
    try {
      await apiJson('PUT', '/api/settings', {
        speak_responses: next ? 'true' : 'false',
      });
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveVoice = async (next: string): Promise<void> => {
    setBusy(true);
    setErr(null);
    setDefaultVoice(next);
    try {
      await apiJson('PUT', '/api/settings', { default_tts_voice: next });
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const testVoice = async (voiceName: string): Promise<void> => {
    setTesting(voiceName);
    setErr(null);
    try {
      const res = await apiJson<SpeakResponse>('POST', '/api/voice/speak', {
        text: `Hello, I am ${voiceName}.`,
        voice: voiceName,
      });
      const absolute = res.signedUrl.startsWith('http')
        ? res.signedUrl
        : `${API_BASE}${res.signedUrl}`;
      await playAudio(absolute);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(null);
    }
  };

  if (!loaded) {
    return <div className="muted">Loading…</div>;
  }

  return (
    <div className="stack">
      <div className="panel">
        <h3>Voice output</h3>
        <label className="voice-toggle-row">
          <input
            type="checkbox"
            checked={speakResponses}
            onChange={(e) => void saveSpeakResponses(e.target.checked)}
            disabled={busy}
          />
          <span>Speak assistant responses automatically</span>
        </label>
        <p className="muted small">
          When enabled, each assistant reply is synthesised with
          gemini-3.1-flash-tts-preview and played automatically. Muting
          via the header speaker icon always wins over this setting.
        </p>
      </div>

      <VoicePresetsPanel onChanged={onChanged} />

      <div className="panel">
        <h3>Default voice</h3>
        <p className="muted small">
          Used when the active persona has no voice of its own. Test any
          voice below to hear a short sample.
        </p>
        <div className="voice-dropdown-row">
          <select
            value={defaultVoice}
            onChange={(e) => void saveVoice(e.target.value)}
            disabled={busy}
          >
            {VOICES.map((v) => (
              <option key={v.name} value={v.name}>
                {v.name} — {v.description}
              </option>
            ))}
          </select>
          <button
            className="btn-test-voice"
            onClick={() => void testVoice(defaultVoice)}
            disabled={testing !== null}
          >
            {testing === defaultVoice ? 'Playing…' : 'Test voice'}
          </button>
        </div>

        <ul className="voice-list">
          {VOICES.map((v) => (
            <li key={v.name} className="voice-list-row">
              <span className="voice-list-name">{v.name}</span>
              <span className="muted small">{v.description}</span>
              <button
                className="btn-test-voice"
                onClick={() => void testVoice(v.name)}
                disabled={testing !== null}
              >
                {testing === v.name ? 'Playing…' : 'Test'}
              </button>
            </li>
          ))}
        </ul>
        {err ? <div className="status-missing">Error: {err}</div> : null}
      </div>
    </div>
  );
}

interface DotSettingsResponse {
  settings: Record<string, string>;
}

function DotTab({ onChanged }: { onChanged: () => void }) {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [activeId, setActiveId] = useState<string>('dot-default-v4');
  const [color, setColor] = useState<string>('#7dd3fc');
  const [wireframe, setWireframe] = useState<boolean>(false);
  const [sensitivity, setSensitivity] = useState<number>(0.3);
  // Stage 45: Large DOT settings.
  const [largeEnabled, setLargeEnabled] = useState<boolean>(false);
  const [largeMoodBg, setLargeMoodBg] = useState<boolean>(true);
  const [largeIdleMode, setLargeIdleMode] = useState<
    'liquid' | 'flow' | 'static'
  >('liquid');
  const [largeWorkingMode, setLargeWorkingMode] = useState<
    'liquid' | 'flow' | 'static'
  >('flow');
  const [loaded, setLoaded] = useState<boolean>(false);
  const [profiles, setProfiles] = useState<DotProfileLite[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [previewCtx, setPreviewCtx] = useState<AudioContext | null>(null);
  const [previewAnalyser, setPreviewAnalyser] =
    useState<AnalyserNode | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiGet<DotSettingsResponse>('/api/settings');
        const s = res.settings || {};
        setEnabled(s.dot_enabled === 'true');
        setActiveId(s.dot_active_profile_id || 'dot-default-v4');
        setColor(s.dot_color || '#7dd3fc');
        setWireframe(s.dot_wireframe === 'true');
        const sn = parseFloat(s.dot_sensitivity ?? '0.3');
        setSensitivity(
          Number.isFinite(sn) ? Math.max(0, Math.min(1, sn)) : 0.3,
        );
        // Stage 45: Large DOT settings hydrate from same response.
        setLargeEnabled(s.dot_large_enabled === 'true');
        setLargeMoodBg(s.dot_large_mood_bg !== 'false');
        setLargeIdleMode(
          s.dot_large_idle_mode === 'flow' ||
            s.dot_large_idle_mode === 'static'
            ? s.dot_large_idle_mode
            : 'liquid',
        );
        setLargeWorkingMode(
          s.dot_large_working_mode === 'liquid' ||
            s.dot_large_working_mode === 'static'
            ? s.dot_large_working_mode
            : 'flow',
        );
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoaded(true);
      }
    })();
    void listDotProfiles()
      .then(setProfiles)
      .catch(() => {
        /* surfaced via err on subsequent action */
      });
  }, []);

  // Stage 43.1: pull the shared speech bus for the in-tab preview so
  // it sees the same audio mix as the header DOT.
  useEffect(() => {
    const bus = getOrCreateSpeechBus();
    setPreviewCtx(bus.ctx);
    setPreviewAnalyser(bus.analyser);
  }, []);

  const save = async (key: string, value: string): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await apiJson('PUT', '/api/settings', { [key]: value });
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setUploadErr(null);
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const text = await f.text();
      const parsed = JSON.parse(text) as { kind?: unknown };
      if (parsed?.kind !== 'supershape3d-cues') {
        throw new Error('Profile JSON missing kind="supershape3d-cues"');
      }
      const name =
        f.name.replace(/\.json$/i, '').slice(0, 80) || 'Untitled';
      const created = await uploadDotProfile(name, parsed);
      const next = await listDotProfiles();
      setProfiles(next);
      setActiveId(created.id);
      await save('dot_active_profile_id', created.id);
    } catch (e2) {
      setUploadErr(e2 instanceof Error ? e2.message : String(e2));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    if (id === 'dot-default-v4') return;
    if (!confirm('Delete this profile? This cannot be undone.')) return;
    setBusy(true);
    try {
      await deleteDotProfile(id);
      const next = await listDotProfiles();
      setProfiles(next);
      if (activeId === id) {
        setActiveId('dot-default-v4');
        await save('dot_active_profile_id', 'dot-default-v4');
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const testVoice = async (): Promise<void> => {
    setErr(null);
    try {
      const res = await apiJson<SpeakResponse>('POST', '/api/voice/speak', {
        text: 'Testing the speech visualization dot.',
      });
      const absolute = res.signedUrl.startsWith('http')
        ? res.signedUrl
        : `${API_BASE}${res.signedUrl}`;
      await playAudio(absolute);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  if (!loaded) return <div className="muted">Loading…</div>;

  const previewProfile = profiles.find((p) => p.id === activeId)?.profile;
  const mergedPreview = previewProfile
    ? {
        ...(previewProfile as object),
        audio: {
          ...((previewProfile as { audio?: object }).audio ?? {}),
          reactiveAmount: sensitivity,
        },
      }
    : null;

  return (
    <div className="stack">
      <div className="panel">
        <h3>Speech visualization</h3>
        <p className="muted small">
          Renders an audio-reactive 3D supershape next to the persona
          header whenever the assistant is speaking. Reads from the
          same TTS audio bus as the Voice tab.
        </p>

        <label className="voice-toggle-row">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              void save(
                'dot_enabled',
                e.target.checked ? 'true' : 'false',
              );
            }}
            disabled={busy}
          />
          <span>Show DOT widget</span>
        </label>

        <div className="dot-color-row">
          <label>
            Color&nbsp;
            <input
              type="color"
              value={color}
              onChange={(e) => {
                setColor(e.target.value);
                void save('dot_color', e.target.value);
              }}
              disabled={busy || !enabled}
            />
          </label>
          <label
            className="voice-toggle-row"
            style={{ marginLeft: 16 }}
          >
            <input
              type="checkbox"
              checked={wireframe}
              onChange={(e) => {
                setWireframe(e.target.checked);
                void save(
                  'dot_wireframe',
                  e.target.checked ? 'true' : 'false',
                );
              }}
              disabled={busy || !enabled}
            />
            <span>Wireframe</span>
          </label>
        </div>

        <div className="mood-weight-row">
          <div className="mood-weight-label">
            Sensitivity: <strong>{sensitivity.toFixed(2)}</strong>
          </div>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={sensitivity}
            disabled={!enabled}
            onChange={(e) => setSensitivity(parseFloat(e.target.value))}
            onMouseUp={(e) =>
              void save(
                'dot_sensitivity',
                (e.currentTarget as HTMLInputElement).value,
              )
            }
            onTouchEnd={(e) =>
              void save(
                'dot_sensitivity',
                (e.currentTarget as HTMLInputElement).value,
              )
            }
          />
          <div className="muted small">
            0 = parameters hold their cue ranges only · 1 = the
            reactive fallback sweeps the full param range. 0.30 is the
            built-in default.
          </div>
        </div>
      </div>

      <div className="panel">
        <h3>Profiles</h3>
        <ul className="persona-list">
          {profiles.map((p) => (
            <li key={p.id} className="persona-list-item">
              <div>
                <div className="persona-list-name">
                  {p.name}
                  {p.id === activeId ? (
                    <span className="badge"> active</span>
                  ) : null}
                </div>
                <div className="muted small">
                  {p.id === 'dot-default-v4'
                    ? 'built-in default'
                    : new Date(p.updated_at * 1000)
                        .toISOString()
                        .slice(0, 10)}
                </div>
              </div>
              <div className="row">
                {p.id !== activeId ? (
                  <button
                    onClick={() => {
                      setActiveId(p.id);
                      void save('dot_active_profile_id', p.id);
                    }}
                    disabled={busy || !enabled}
                  >
                    Set active
                  </button>
                ) : null}
                {p.id !== 'dot-default-v4' ? (
                  <button
                    onClick={() => void onDelete(p.id)}
                    disabled={busy}
                  >
                    Delete
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>

        <div className="row" style={{ marginTop: 12 }}>
          <button
            className="btn-primary"
            onClick={() => fileRef.current?.click()}
            disabled={busy || !enabled}
          >
            Upload profile JSON
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={(e) => void onUpload(e)}
          />
          <button
            onClick={() => void testVoice()}
            disabled={busy || !enabled}
          >
            Test voice (preview)
          </button>
        </div>
        {uploadErr ? (
          <div className="status-missing">Upload error: {uploadErr}</div>
        ) : null}
      </div>

      {/* Stage 45: Large DOT sidebar widget controls. The widget
          replaces the small header DOT on the Chat view only and
          adds a mood-tinted background plus idle/working ambient
          modes. The supershape itself reuses the active profile
          chosen above. */}
      <div className="panel">
        <h3>Large DOT</h3>
        <p className="muted small">
          Replaces the header DOT with a larger sticky widget on
          the right edge of the Chat view. Reuses the same active
          profile above; adds a mood-tinted background and
          idle / working ambient modes driven by the orchestrator.
        </p>

        <label className="voice-toggle-row">
          <input
            type="checkbox"
            checked={largeEnabled}
            onChange={(e) => {
              setLargeEnabled(e.target.checked);
              void save(
                'dot_large_enabled',
                e.target.checked ? 'true' : 'false',
              );
            }}
            disabled={busy}
          />
          <span>Show large DOT in sidebar (Chat view only)</span>
        </label>

        <label className="voice-toggle-row">
          <input
            type="checkbox"
            checked={largeMoodBg}
            onChange={(e) => {
              setLargeMoodBg(e.target.checked);
              void save(
                'dot_large_mood_bg',
                e.target.checked ? 'true' : 'false',
              );
            }}
            disabled={busy || !largeEnabled}
          />
          <span>Mood-tinted background</span>
        </label>

        <div className="dot-color-row">
          <label>
            Idle mode&nbsp;
            <select
              value={largeIdleMode}
              onChange={(e) => {
                const v = e.target.value as 'liquid' | 'flow' | 'static';
                setLargeIdleMode(v);
                void save('dot_large_idle_mode', v);
              }}
              disabled={busy || !largeEnabled}
            >
              <option value="liquid">01 liquid (slow drift)</option>
              <option value="flow">02 flow (active sweep)</option>
              <option value="static">static (no animation)</option>
            </select>
          </label>
          <label style={{ marginLeft: 16 }}>
            Working mode&nbsp;
            <select
              value={largeWorkingMode}
              onChange={(e) => {
                const v = e.target.value as 'liquid' | 'flow' | 'static';
                setLargeWorkingMode(v);
                void save('dot_large_working_mode', v);
              }}
              disabled={busy || !largeEnabled}
            >
              <option value="liquid">01 liquid (slow drift)</option>
              <option value="flow">02 flow (active sweep)</option>
              <option value="static">static (no animation)</option>
            </select>
          </label>
        </div>

        <div className="muted small" style={{ marginTop: 8 }}>
          "Idle" applies whenever the assistant is not generating a
          reply. "Working" applies while the assistant is thinking.
          Both are purely visual — neither changes the supershape
          itself.
        </div>
      </div>

      {enabled ? (
        <div className="panel dot-preview">
          <h3>Preview</h3>
          <div className="dot-preview-stage">
            <Suspense
              fallback={<div className="muted small">Loading 3D…</div>}
            >
              <DotTabSupershapeDot
                profile={mergedPreview as never}
                analyser={previewAnalyser}
                audioContext={previewCtx}
                color={color}
                wireframe={wireframe}
                autoRotate
              />
            </Suspense>
          </div>
          <p className="muted small">
            Click <em>Test voice</em> above to drive the dot from a
            short Gemini TTS sample.
          </p>
        </div>
      ) : null}

      {err ? <div className="status-missing">Error: {err}</div> : null}
    </div>
  );
}

